import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CAMPAIGN_AUDIENCE_LIMITS,
  CAMPAIGN_MESSAGE_TEMPLATE_LIMITS,
  DEFAULT_NOTIFICATION_AUTO_REPLY,
  type AudienceReachabilityPreview,
  type CampaignAudienceMode,
  type CampaignChannelKind,
  type CampaignDeliveryMode,
  type CampaignPurpose,
  type CampaignTemplateVariableConfig,
  type ContactAgent,
  type ContactRecord,
  type SegmentFilter,
  type WhatsAppChannel,
  type WhatsAppTemplate,
} from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { hasModuleFeature } from '@/features/auth/moduleFeatures';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { whatsappAdminApi } from '@/features/whatsapp/api/whatsappApi';
import { cloneCampaignDraft } from '../api/campaignClone';
import {
  campaignChannelOptions,
  unionInstanceChannels,
} from '../api/campaignChannelAvailability';
import { CAMPAIGNS_HUB_PATH, campaignDetailPath } from '../api/campaignPaths';
import {
  clearCampaignWizardSnapshot,
  loadCampaignWizardSnapshot,
  saveCampaignWizardSnapshot,
} from '../api/campaignWizardStorage';
import {
  campaignEndsAtIsoFromDateInput,
  defaultCampaignEndDateInput,
  minCampaignEndDateInput,
  suggestedCampaignName,
  validateCampaignEndDateInput,
  wizardSubmitError,
} from '../api/campaignWizardRules';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import {
  contactMatchesCobranzasSegment,
  excludedContactIdsFromPreview,
  previewWithInclusions,
  reachableContactIds,
} from '../api/campaignAudienceInclusion';
import { ContactAgentCampaignScheduleFields } from '../components/ContactAgentCampaignScheduleFields';
import {
  DEFAULT_CAMPAIGN_SCHEDULE,
  schedulePayload,
  type CampaignScheduleState,
} from '../components/ContactAgentCampaignScheduleFields.utils';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { ContactAgentTemplateVariableMapper } from '../components/ContactAgentTemplateVariableMapper';
import { ensureTemplateVariableConfig } from '../components/ContactAgentTemplateVariableMapper.utils';
import { CampaignTestSendPanel } from '../components/CampaignTestSendPanel';
import { CampaignWizardAxisCards } from '../components/CampaignWizardAxisCards';
import { CampaignWizardAudienceStep } from '../components/CampaignWizardAudienceStep';
import { CampaignWizardReview } from '../components/CampaignWizardReview';
import { CampaignWizardStepper } from '../components/CampaignWizardStepper';
import { MessageTemplatePicker } from '../components/MessageTemplatePicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

const STEPS = ['Propósito', 'Modo', 'Canal', 'Público', 'Plantilla', 'Agenda', 'Revisión'];

export function ContactAgentCampaignNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneId = (searchParams.get('from') ?? '').slice(0, 64);
  const currentUser = useAppSelector(selectCurrentUser);
  const actorRole = currentUser?.role ?? 'USER';
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [step, setStep] = useState(1);
  const [agents, setAgents] = useState<ContactAgent[]>([]);
  const [channels, setChannels] = useState<WhatsAppChannel[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [contacts, setContacts] = useState<ContactRecord[]>([]);
  const [instanceChannels, setInstanceChannels] = useState<Array<'WHATSAPP' | 'SMS' | 'EMAIL'>>(['WHATSAPP']);
  const [agentId, setAgentId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [name, setName] = useState('');
  const [initialTemplateId, setInitialTemplateId] = useState('');
  const [retryTemplateId, setRetryTemplateId] = useState('');
  const [retryWaitHours, setRetryWaitHours] = useState(24);
  const [templateVariableMap, setTemplateVariableMap] = useState<CampaignTemplateVariableConfig>({});
  const [purpose, setPurpose] = useState<CampaignPurpose>('COBRANZAS');
  const [deliveryMode, setDeliveryMode] = useState<CampaignDeliveryMode>('CONVERSATIONAL');
  const [channelKind, setChannelKind] = useState<CampaignChannelKind>('WHATSAPP');
  const [messageTemplateId, setMessageTemplateId] = useState('');
  const [autoReplyText, setAutoReplyText] = useState('');
  const [audienceMode, setAudienceMode] = useState<CampaignAudienceMode>('SEGMENT');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [schedule, setSchedule] = useState<CampaignScheduleState>(DEFAULT_CAMPAIGN_SCHEDULE);
  const [maxContactsPerDay, setMaxContactsPerDay] = useState(100);
  const [endsAtDate, setEndsAtDate] = useState(defaultCampaignEndDateInput);
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>({ overdueOnly: true });
  const [reachPreview, setReachPreview] = useState<AudienceReachabilityPreview | null>(null);
  const [previewIncludedIds, setPreviewIncludedIds] = useState<string[]>([]);
  const [advancedSchedule, setAdvancedSchedule] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const tenantHasSms = hasModuleFeature(currentUser, 'sms-integration');
  const tenantHasEmail = hasModuleFeature(currentUser, 'email-integration');
  const channelOptions = useMemo(
    () =>
      campaignChannelOptions({
        deliveryMode,
        tenantHasSms,
        tenantHasEmail,
        instanceChannels,
      }),
    [deliveryMode, instanceChannels, tenantHasEmail, tenantHasSms],
  );

  const selectedWhatsAppChannel = channels.find(channel => channel.id === channelId);
  const whatsAppCampaignTemplates = useMemo(() => {
    if (!channelId) {
      return [];
    }
    if (selectedWhatsAppChannel?.provider === 'WAHA') {
      return templates.filter(
        template => template.origin === 'LOCAL' && template.status === 'ACTIVE',
      );
    }
    return templates;
  }, [channelId, selectedWhatsAppChannel?.provider, templates]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    void loadCampaignForm(cloneId, tenantId)
      .then(loaded => {
        setAgents(loaded.agents);
        setChannels(loaded.channels);
        setContacts(loaded.contacts);
        setInstanceChannels(loaded.instanceChannels);
        setChannelId(loaded.channelId);
        setAgentId(
          loaded.draft && loaded.draft.deliveryMode !== 'CONVERSATIONAL' ? '' : loaded.agentId,
        );
        if (loaded.draft) {
          setName(loaded.draft.name);
          setPurpose(loaded.draft.purpose);
          setDeliveryMode(loaded.draft.deliveryMode);
          setChannelKind(loaded.draft.channelKind);
          setMessageTemplateId(loaded.draft.messageTemplateId);
          setInitialTemplateId(loaded.draft.initialTemplateId);
          setRetryTemplateId(loaded.draft.retryTemplateId);
          setRetryWaitHours(loaded.draft.retryWaitHours);
          setTemplateVariableMap(loaded.draft.templateVariableMap);
          setMaxContactsPerDay(loaded.draft.maxContactsPerDay);
          setSegmentFilter(loaded.draft.segmentFilter);
          setSchedule(loaded.draft.schedule);
          setAutoReplyText(loaded.draft.autoReplyText);
          setEndsAtDate(loaded.draft.endsAtDate);
        }
        const snapshot = loadCampaignWizardSnapshot();
        if (snapshot) {
          setName(snapshot.name);
          setAgentId(snapshot.deliveryMode === 'CONVERSATIONAL' ? snapshot.agentId || loaded.agentId : '');
          setChannelId(snapshot.channelId || loaded.channelId);
          setPurpose(snapshot.purpose);
          setDeliveryMode(snapshot.deliveryMode);
          setChannelKind(snapshot.channelKind);
          setMessageTemplateId(snapshot.messageTemplateId);
          setAudienceMode(snapshot.audienceMode);
          setMaxContactsPerDay(snapshot.maxContactsPerDay);
          setAutoReplyText(snapshot.autoReplyText);
          if (snapshot.retryTemplateId) {
            setRetryTemplateId(snapshot.retryTemplateId);
          }
          if (snapshot.retryWaitHours) {
            setRetryWaitHours(snapshot.retryWaitHours);
          }
          if (snapshot.endsAtDate) {
            setEndsAtDate(snapshot.endsAtDate);
          }
          clearCampaignWizardSnapshot();
        }
        applySearchPresets(
          searchParams,
          setChannelKind,
          setDeliveryMode,
          setMessageTemplateId,
          setAutoReplyText,
          Boolean((snapshot?.autoReplyText || loaded.draft?.autoReplyText || '').trim()),
        );
      })
      .catch(err => setError(contactAgentError(err)));
  }, [ready, tenantId, cloneId, searchParams]);

  useEffect(() => {
    if (!channelId) {
      setTemplates([]);
      return;
    }
    void whatsappAdminApi
      .listTemplates(channelId)
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [channelId]);

  useEffect(() => {
    setReachPreview(null);
    setPreviewIncludedIds([]);
  }, [segmentFilter, audienceMode, purpose, channelKind, selectedIds]);

  useEffect(() => {
    if (purpose !== 'COBRANZAS') {
      return;
    }
    setSelectedIds(current =>
      current.filter(id => {
        const contact = contacts.find(row => row.id === id);
        return contact ? contactMatchesCobranzasSegment(contact, segmentFilter) : false;
      }),
    );
  }, [segmentFilter, contacts, purpose]);

  const preview = async () => {
    try {
      const result = await contactAgentApi.previewReachability({
        purpose,
        channelKind,
        agentId: agentId || agents[0]?.id || undefined,
        segmentFilter: purpose === 'COBRANZAS' ? segmentFilter : {},
        audienceMode,
        contactIds: audienceMode === 'EXPLICIT' ? selectedIds : undefined,
      });
      setReachPreview(result);
      setPreviewIncludedIds(reachableContactIds(result.reachablePreview));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo calcular el segmento');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (step < 7) {
      setStep(step + 1);
      return;
    }
    const draftName = name.trim() || suggestedCampaignName({ purpose, deliveryMode, channelKind });
    const needsAgent = deliveryMode !== 'NOTIFICATION';
    const isWhatsApp = channelKind === 'WHATSAPP';
    const submitError = wizardSubmitError({
      needsAgent,
      isWhatsApp,
      agentId,
      channelId,
      name: draftName,
      initialTemplateId,
      messageTemplateId,
    });
    if (submitError) {
      setError(submitError);
      return;
    }
    const endsAtError = validateCampaignEndDateInput(endsAtDate);
    if (endsAtError) {
      setError(endsAtError);
      return;
    }
    setSaving(true);
    try {
      const campaign = await persistDraft({
        draftName,
        deliveryMode,
        channelKind,
        agentId,
        channelId,
        messageTemplateId,
        purpose,
        audienceMode,
        schedule,
        maxContactsPerDay,
        initialTemplateId,
        retryTemplateId,
        retryWaitHours,
        templateVariableMap,
        templates,
        segmentFilter,
        selectedIds,
        excludedContactIds:
          audienceMode === 'SEGMENT'
            ? excludedContactIdsFromPreview(reachPreview?.reachablePreview, previewIncludedIds)
            : [],
        autoReplyText,
        endsAtDate,
      });
      navigate(campaignDetailPath(campaign.id));
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDeliveryMode = (next: CampaignDeliveryMode) => {
    setDeliveryMode(next);
    if (next === 'CONVERSATIONAL') {
      setChannelKind('WHATSAPP');
      if (!agentId && agents.length === 1) {
        setAgentId(agents[0].id);
      }
    } else {
      setAgentId('');
      if (!autoReplyText.trim()) {
        setAutoReplyText(DEFAULT_NOTIFICATION_AUTO_REPLY);
      }
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        eyebrow='Campañas'
        backTo={CAMPAIGNS_HUB_PATH}
        title={cloneId ? 'Clonar campaña' : 'Nueva campaña'}
        description='Se guarda como borrador. El envío empieza solo cuando una persona inicia la campaña.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />
      <CampaignWizardStepper steps={STEPS} current={step} />
      <Card className='rounded-xl shadow-sm'>
        <CardHeader>
          <CardTitle>{STEPS[step - 1]}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className='grid gap-6 p-2 md:grid-cols-2' onSubmit={handleSubmit}>
            {step === 1 ? (
              <CampaignWizardAxisCards
                legend='Qué se dice'
                value={purpose}
                onChange={setPurpose}
                options={[
                  { id: 'COBRANZAS', title: 'Cobranzas', description: 'Gestión de deuda y compromisos de pago.' },
                  { id: 'AVISO', title: 'Aviso', description: 'Un mensaje informativo, sin gestión de deuda.' },
                ]}
              />
            ) : null}
            {step === 2 ? (
              <CampaignWizardAxisCards
                legend='Cómo aterriza'
                value={deliveryMode}
                onChange={onDeliveryMode}
                options={[
                  { id: 'CONVERSATIONAL', title: 'Conversacional', description: 'Abre un diálogo en el bot' },
                  { id: 'NOTIFICATION', title: 'Notificación', description: 'Un mensaje. No espera respuesta.' },
                ]}
              />
            ) : null}
            {step === 3 ? (
              <ChannelStep
                channelKind={channelKind}
                channelOptions={channelOptions}
                deliveryMode={deliveryMode}
                agentId={agentId}
                agents={agents}
                channelId={channelId}
                channels={channels}
                onChannelKind={kind => {
                  setChannelKind(kind);
                  setMessageTemplateId('');
                }}
                onAgentId={setAgentId}
                onChannelId={setChannelId}
              />
            ) : null}
            {step === 4 ? (
              <CampaignWizardAudienceStep
                purpose={purpose}
                channelKind={channelKind}
                audienceMode={audienceMode}
                onAudienceMode={setAudienceMode}
                segmentFilter={segmentFilter}
                onSegmentFilter={setSegmentFilter}
                contacts={contacts}
                selectedIds={selectedIds}
                onSelectedIds={setSelectedIds}
                reachPreview={reachPreview}
                onPreview={() => void preview()}
                includedIds={previewIncludedIds}
                onIncludedIds={setPreviewIncludedIds}
              />
            ) : null}
            {step === 5 ? (
              <TemplateStep
                channelKind={channelKind}
                deliveryMode={deliveryMode}
                messageTemplateId={messageTemplateId}
                initialTemplateId={initialTemplateId}
                retryTemplateId={retryTemplateId}
                retryWaitHours={retryWaitHours}
                templates={whatsAppCampaignTemplates}
                isWahaChannel={selectedWhatsAppChannel?.provider === 'WAHA'}
                purpose={purpose}
                templateVariableMap={templateVariableMap}
                actorRole={actorRole}
                channelId={channelId}
                name={name}
                agentId={agentId}
                audienceMode={audienceMode}
                maxContactsPerDay={maxContactsPerDay}
                autoReplyText={autoReplyText}
                onMessageTemplateId={setMessageTemplateId}
                onInitialTemplateId={setInitialTemplateId}
                onRetryTemplateId={setRetryTemplateId}
                onRetryWaitHours={setRetryWaitHours}
                onTemplateVariableMap={setTemplateVariableMap}
                onAutoReplyText={setAutoReplyText}
                endsAtDate={endsAtDate}
              />
            ) : null}
            {step === 6 ? (
              <ScheduleStep
                maxContactsPerDay={maxContactsPerDay}
                onMaxContactsPerDay={setMaxContactsPerDay}
                endsAtDate={endsAtDate}
                onEndsAtDate={setEndsAtDate}
                schedule={schedule}
                onSchedule={setSchedule}
                advanced={advancedSchedule}
                onAdvanced={setAdvancedSchedule}
              />
            ) : null}
            {step === 7 ? (
              <CampaignWizardReview
                name={name}
                onName={setName}
                purpose={purpose}
                deliveryMode={deliveryMode}
                channelKind={channelKind}
                templateLabel={channelKind === 'WHATSAPP' ? initialTemplateId || 'sin definir' : 'biblioteca'}
                whenLabel={`Vence ${endsAtDate} · ${schedule.weekdayFrom}–${schedule.weekdayTo} · tope ${maxContactsPerDay}/día`}
                reachPreview={
                  reachPreview && audienceMode === 'SEGMENT'
                    ? previewWithInclusions(reachPreview, previewIncludedIds)
                    : reachPreview
                }
                autoReplyText={deliveryMode === 'NOTIFICATION' ? autoReplyText : ''}
              />
            ) : null}
            {error ? <p className='md:col-span-2 text-sm text-destructive'>{error}</p> : null}
            <div className='md:col-span-2 flex flex-wrap justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => (step === 1 ? navigate(CAMPAIGNS_HUB_PATH) : setStep(step - 1))}
              >
                {step === 1 ? 'Cancelar' : 'Atrás'}
              </Button>
              {step === 7 ? (
                <Button type='button' variant='ghost' onClick={() => navigate(CAMPAIGNS_HUB_PATH)}>
                  Cancelar
                </Button>
              ) : null}
              <Button type='submit' disabled={saving}>
                {saving ? 'Guardando...' : step < 7 ? 'Siguiente' : 'Guardar borrador'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}

function ChannelStep({
  channelKind,
  channelOptions,
  deliveryMode,
  agentId,
  agents,
  channelId,
  channels,
  onChannelKind,
  onAgentId,
  onChannelId,
}: {
  channelKind: CampaignChannelKind;
  channelOptions: ReturnType<typeof campaignChannelOptions>;
  deliveryMode: CampaignDeliveryMode;
  agentId: string;
  agents: ContactAgent[];
  channelId: string;
  channels: WhatsAppChannel[];
  onChannelKind: (kind: CampaignChannelKind) => void;
  onAgentId: (id: string) => void;
  onChannelId: (id: string) => void;
}) {
  return (
    <div className='md:col-span-2 space-y-6'>
      <CampaignWizardAxisCards
        legend='Por dónde sale'
        value={channelKind}
        onChange={onChannelKind}
        options={channelOptions.map(option => ({
          id: option.kind,
          title: option.label,
          description:
            option.kind === 'WHATSAPP'
              ? 'Plantilla de WhatsApp aprobada.'
              : option.kind === 'EMAIL' && deliveryMode === 'CONVERSATIONAL'
                ? 'Sale por email con un link al chat. El deudor conversa en la web.'
                : `Un mensaje por ${option.label}.`,
          enabled: option.enabled,
          reason: option.reason,
        }))}
      />
      {deliveryMode === 'CONVERSATIONAL' ? (
        <Field label='Agente' htmlFor='agentId'>
          <NativeSelect
            id='agentId'
            value={agentId}
            onChange={onAgentId}
            options={agents.map(agent => ({ id: agent.id, label: agent.name }))}
            empty='Todavía no hay un agente'
          />
        </Field>
      ) : (
        <p className='text-sm text-muted-foreground'>La notificación no necesita un agente de contacto.</p>
      )}
      {channelKind === 'WHATSAPP' ? (
        <Field label='Canal de WhatsApp de contacto' htmlFor='channelId'>
          <NativeSelect
            id='channelId'
            value={channelId}
            onChange={onChannelId}
            options={channels.map(channel => ({
              id: channel.id,
              label: channel.displayPhoneNumber || channel.phoneNumberId,
            }))}
            empty='No hay canales de contacto'
          />
        </Field>
      ) : (
        <p className='text-sm text-muted-foreground'>
          Esta campaña sale por {channelKind === 'EMAIL' ? 'email' : 'SMS'}. El canal se toma de la instancia.
        </p>
      )}
    </div>
  );
}

function TemplateStep({
  channelKind,
  deliveryMode,
  messageTemplateId,
  initialTemplateId,
  retryTemplateId,
  retryWaitHours,
  templates,
  isWahaChannel,
  purpose,
  templateVariableMap,
  actorRole,
  channelId,
  name,
  agentId,
  audienceMode,
  maxContactsPerDay,
  autoReplyText,
  onMessageTemplateId,
  onInitialTemplateId,
  onRetryTemplateId,
  onRetryWaitHours,
  onTemplateVariableMap,
  onAutoReplyText,
  endsAtDate,
}: {
  channelKind: CampaignChannelKind;
  deliveryMode: CampaignDeliveryMode;
  messageTemplateId: string;
  initialTemplateId: string;
  retryTemplateId: string;
  retryWaitHours: number;
  templates: WhatsAppTemplate[];
  isWahaChannel?: boolean;
  purpose: CampaignPurpose;
  templateVariableMap: CampaignTemplateVariableConfig;
  actorRole: string;
  channelId: string;
  name: string;
  agentId: string;
  audienceMode: CampaignAudienceMode;
  maxContactsPerDay: number;
  autoReplyText: string;
  onMessageTemplateId: (id: string) => void;
  onInitialTemplateId: (id: string) => void;
  onRetryTemplateId: (id: string) => void;
  onRetryWaitHours: (hours: number) => void;
  onTemplateVariableMap: (value: CampaignTemplateVariableConfig) => void;
  onAutoReplyText: (value: string) => void;
  endsAtDate: string;
}) {
  const persist = () =>
    saveCampaignWizardSnapshot({
      name,
      agentId,
      channelId,
      purpose,
      deliveryMode,
      channelKind,
      messageTemplateId,
      audienceMode,
      maxContactsPerDay,
      autoReplyText,
      retryTemplateId,
      retryWaitHours,
      endsAtDate,
    });
  const emailConversational = channelKind === 'EMAIL' && deliveryMode === 'CONVERSATIONAL';
  return (
    <div className='md:col-span-2 space-y-6'>
      {emailConversational ? (
        <>
          <p className='text-sm text-muted-foreground'>
            Sale por email con un link al chat. El deudor conversa en la web. La plantilla debe incluir{' '}
            <code>{'{{campaign.accessUrl}}'}</code>.
          </p>
          <Field label='Plantilla de email inicial' htmlFor='messageTemplateId'>
            <MessageTemplatePicker
              channelKind='EMAIL'
              value={messageTemplateId}
              onChange={onMessageTemplateId}
              onPersistWizard={persist}
            />
          </Field>
          <Field label='Reintento por mail (opcional)' htmlFor='retryTemplateId'>
            <MessageTemplatePicker
              channelKind='EMAIL'
              value={retryTemplateId}
              onChange={onRetryTemplateId}
              onPersistWizard={persist}
            />
          </Field>
          {retryTemplateId.trim() ? (
            <Field label='Espera antes del reintento (horas)' htmlFor='retryWaitHours'>
              <Input
                id='retryWaitHours'
                type='number'
                min={1}
                max={720}
                value={retryWaitHours}
                onChange={event => onRetryWaitHours(Number(event.target.value) || 24)}
              />
            </Field>
          ) : null}
        </>
      ) : channelKind !== 'WHATSAPP' ? (
        <MessageTemplatePicker
          channelKind={channelKind === 'EMAIL' ? 'EMAIL' : 'SMS'}
          value={messageTemplateId}
          onChange={onMessageTemplateId}
          onPersistWizard={persist}
        />
      ) : (
        <>
          <Field
            label={isWahaChannel ? 'Plantilla del canal' : 'Plantilla de WhatsApp'}
            htmlFor='initialTemplateId'
          >
            <TemplateField id='initialTemplateId' value={initialTemplateId} templates={templates} onChange={onInitialTemplateId} />
          </Field>
          <ContactAgentTemplateVariableMapper
            templateName={initialTemplateId}
            templates={templates}
            purpose={purpose}
            value={templateVariableMap}
            onChange={onTemplateVariableMap}
          />
          {deliveryMode === 'CONVERSATIONAL' ? (
            <>
              <Field label='Reintento (opcional)' htmlFor='retryTemplateId'>
                <TemplateField id='retryTemplateId' value={retryTemplateId} templates={templates} onChange={onRetryTemplateId} />
              </Field>
              {retryTemplateId.trim() ? (
                <Field label='Espera antes del reintento (horas)' htmlFor='retryWaitHours'>
                  <Input
                    id='retryWaitHours'
                    type='number'
                    min={1}
                    max={720}
                    value={retryWaitHours}
                    onChange={event => onRetryWaitHours(Number(event.target.value) || 24)}
                  />
                </Field>
              ) : null}
            </>
          ) : (
            <p className='text-sm text-muted-foreground'>La notificación es un solo mensaje: no hay reintentos de diálogo.</p>
          )}
        </>
      )}
      {deliveryMode === 'NOTIFICATION' ? (
        <Field label='Respuesta automática' htmlFor='autoReplyText'>
          <Textarea
            id='autoReplyText'
            value={autoReplyText}
            maxLength={CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.autoReplyMax}
            rows={12}
            onChange={event => onAutoReplyText(event.target.value.slice(0, CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.autoReplyMax))}
          />
          <p className='text-sm text-muted-foreground'>
            Si alguien contesta el aviso, se envía este texto una sola vez. Reemplazá teléfono, horario y
            dirección. Vacío = no se responde. BAJA se atiende aparte.
          </p>
        </Field>
      ) : null}
      <CampaignTestSendPanel
        collapsed
        actorRole={actorRole}
        channelKind={channelKind}
        agentId={channelKind === 'EMAIL' ? agentId || undefined : undefined}
        whatsAppChannelId={channelKind === 'WHATSAPP' ? channelId || undefined : undefined}
        templateId={channelKind === 'WHATSAPP' ? initialTemplateId || undefined : messageTemplateId || undefined}
        templateReadOnly
      />
    </div>
  );
}

function ScheduleStep({
  maxContactsPerDay,
  onMaxContactsPerDay,
  endsAtDate,
  onEndsAtDate,
  schedule,
  onSchedule,
  advanced,
  onAdvanced,
}: {
  maxContactsPerDay: number;
  onMaxContactsPerDay: (value: number) => void;
  endsAtDate: string;
  onEndsAtDate: (value: string) => void;
  schedule: CampaignScheduleState;
  onSchedule: (value: CampaignScheduleState) => void;
  advanced: boolean;
  onAdvanced: (value: boolean) => void;
}) {
  return (
    <>
      <Field label='Fecha de fin' htmlFor='endsAtDate'>
        <Input
          id='endsAtDate'
          type='date'
          min={minCampaignEndDateInput()}
          value={endsAtDate}
          onChange={event => onEndsAtDate(event.target.value.slice(0, 10))}
        />
      </Field>
      <Field label='Tope diario' htmlFor='maxContactsPerDay'>
        <Input
          id='maxContactsPerDay'
          type='number'
          min={1}
          max={5000}
          value={maxContactsPerDay}
          onChange={event => onMaxContactsPerDay(Number(event.target.value) || 1)}
        />
      </Field>
      <div className='md:col-span-2'>
        {advanced ? (
          <ContactAgentCampaignScheduleFields value={schedule} onChange={onSchedule} />
        ) : (
          <Button type='button' variant='ghost' onClick={() => onAdvanced(true)}>
            Más opciones
          </Button>
        )}
      </div>
    </>
  );
}

function applySearchPresets(
  searchParams: URLSearchParams,
  setChannelKind: (kind: CampaignChannelKind) => void,
  setDeliveryMode: (mode: CampaignDeliveryMode) => void,
  setMessageTemplateId: (id: string) => void,
  setAutoReplyText: (value: string) => void,
  keepAutoReply: boolean,
) {
  const presetKind = searchParams.get('channelKind');
  const presetTemplate = (searchParams.get('messageTemplateId') ?? '').slice(0, 64);
  if (presetKind === 'SMS' || presetKind === 'EMAIL') {
    setChannelKind(presetKind);
    setDeliveryMode('NOTIFICATION');
    if (!keepAutoReply) {
      setAutoReplyText(DEFAULT_NOTIFICATION_AUTO_REPLY);
    }
  }
  if (presetTemplate) {
    setMessageTemplateId(presetTemplate);
  }
}

async function persistDraft(input: {
  draftName: string;
  deliveryMode: CampaignDeliveryMode;
  channelKind: CampaignChannelKind;
  agentId: string;
  channelId: string;
  messageTemplateId: string;
  purpose: CampaignPurpose;
  audienceMode: CampaignAudienceMode;
  schedule: CampaignScheduleState;
  maxContactsPerDay: number;
  initialTemplateId: string;
  retryTemplateId: string;
  retryWaitHours: number;
  templateVariableMap: CampaignTemplateVariableConfig;
  templates: WhatsAppTemplate[];
  segmentFilter: SegmentFilter;
  selectedIds: string[];
  excludedContactIds: string[];
  autoReplyText: string;
  endsAtDate: string;
}) {
  const isWhatsApp = input.channelKind === 'WHATSAPP';
  const isEmailConv = input.channelKind === 'EMAIL' && input.deliveryMode === 'CONVERSATIONAL';
  const steps =
    input.deliveryMode === 'NOTIFICATION' || !input.retryTemplateId.trim()
      ? []
      : [
          {
            stepOrder: isEmailConv ? 0 : 1,
            waitHours: input.retryWaitHours,
            templateId: input.retryTemplateId.trim().slice(0, 128),
          },
        ];
  const templateNames = isWhatsApp ? [input.initialTemplateId.trim()] : [];
  if (isWhatsApp && input.deliveryMode !== 'NOTIFICATION' && input.retryTemplateId.trim()) {
    templateNames.push(input.retryTemplateId.trim());
  }
  const campaign = await contactAgentApi.createCampaign({
    agentId: input.agentId || undefined,
    whatsAppChannelId: isWhatsApp ? input.channelId : undefined,
    messageTemplateId: isWhatsApp ? undefined : input.messageTemplateId,
    name: input.draftName.slice(0, 255),
    purpose: input.purpose,
    deliveryMode: input.deliveryMode,
    channelKind: input.channelKind,
    audienceMode: input.audienceMode,
    ...schedulePayload(input.schedule),
    maxContactsPerDay: input.maxContactsPerDay,
    initialTemplateId: isWhatsApp ? input.initialTemplateId.trim().slice(0, 128) : undefined,
    templateVariableMap: isWhatsApp
      ? ensureTemplateVariableConfig({
          config: input.templateVariableMap,
          templateNames,
          templates: input.templates,
          purpose: input.purpose,
        })
      : undefined,
    autoReplyText: input.deliveryMode === 'NOTIFICATION' ? input.autoReplyText.trim().slice(0, CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.autoReplyMax) || null : undefined,
    segmentFilter: filterWithExclusions(
      input.purpose === 'COBRANZAS' ? input.segmentFilter : undefined,
      input.excludedContactIds,
    ),
    endsAt: campaignEndsAtIsoFromDateInput(input.endsAtDate),
    steps,
  });
  if (input.audienceMode === 'EXPLICIT' && input.selectedIds.length > 0) {
    await contactAgentApi.replaceAudience(campaign.id, {
      expectedRevision: campaign.audienceRevision ?? 1,
      mode: 'EXPLICIT',
      members: input.selectedIds.slice(0, CAMPAIGN_AUDIENCE_LIMITS.maxMembersPerRequest).map(contactId => ({ contactId })),
    });
  } else if (input.audienceMode === 'SEGMENT') {
    await contactAgentApi.replaceAudience(campaign.id, {
      expectedRevision: campaign.audienceRevision ?? 1,
      mode: 'SEGMENT',
      segmentFilter: filterWithExclusions(
        input.purpose === 'COBRANZAS' ? input.segmentFilter : undefined,
        input.excludedContactIds,
      ),
    });
  }
  return campaign;
}

function filterWithExclusions(
  filter: SegmentFilter | undefined,
  excludedContactIds: string[],
): SegmentFilter | undefined {
  if (excludedContactIds.length === 0) {
    return filter;
  }
  return { ...(filter ?? {}), excludedContactIds };
}

async function loadCampaignForm(cloneId: string, tenantId?: string) {
  const [agentList, channelList, contactPage, instances] = await Promise.all([
    contactAgentApi.listAgents(),
    whatsappAdminApi.listChannels(tenantId),
    contactAgentApi.listContacts(CAMPAIGN_AUDIENCE_LIMITS.maxImportRows, 0),
    chatbotAdminApi.listInstances(tenantId).catch(() => []),
  ]);
  const channels = channelList.filter(channel => channel.purpose === 'CONTACTO');
  const base = {
    agents: agentList,
    channels,
    contacts: contactPage.items,
    instanceChannels: unionInstanceChannels(instances),
    agentId: agentList[0]?.id ?? '',
    channelId: channels[0]?.id ?? '',
  };
  if (!cloneId) {
    return { ...base, draft: null };
  }
  const source = await contactAgentApi.getCampaign(cloneId);
  const draft = cloneCampaignDraft(source);
  return {
    ...base,
    agentId: agentList.some(agent => agent.id === draft.agentId) ? draft.agentId : base.agentId,
    channelId: channels.some(channel => channel.id === draft.channelId) ? draft.channelId : base.channelId,
    draft,
  };
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className='space-y-2'>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function NativeSelect({
  id,
  value,
  onChange,
  options,
  empty,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ id: string; label: string }>;
  empty?: string;
}) {
  return (
    <select
      id={id}
      className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
      value={value}
      onChange={event => onChange(event.target.value)}
    >
      {options.length === 0 ? <option value=''>{empty ?? 'Sin opciones'}</option> : null}
      {options.map(option => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function TemplateField({
  id,
  value,
  templates,
  onChange,
}: {
  id: string;
  value: string;
  templates: WhatsAppTemplate[];
  onChange: (value: string) => void;
}) {
  return (
    <>
      <Input id={id} list={`${id}-list`} value={value} maxLength={128} onChange={event => onChange(event.target.value)} />
      <datalist id={`${id}-list`}>
        {templates.map(template => (
          <option key={template.id} value={template.name} />
        ))}
      </datalist>
    </>
  );
}
